/**
 * FAQs Section Script
 * 
 * This script handles the functionality for the FAQs section, including:
 * - Tab switching and keyboard navigation
 * - Accordion toggling and keyboard navigation
 * - Accessibility features
 */

class FaqsSection extends HTMLElement {
  constructor() {
    super();
    this.tabButtons = this.querySelectorAll('.faqs__tab-button');
    this.tabContents = this.querySelectorAll('.faqs__tab-content');
    this.accordionHeaders = this.querySelectorAll('.faqs__accordion-header');
    
    this.init();
  }

  init() {
    // Initialize tab functionality
    this.tabButtons.forEach(button => {
      button.addEventListener('click', () => this.activateTab(button));
      
      // Keyboard navigation for tabs
      button.addEventListener('keydown', this.handleTabKeydown.bind(this));
    });

    // Initialize accordion functionality
    this.accordionHeaders.forEach(header => {
      header.addEventListener('click', () => this.toggleAccordion(header));
      
      // Keyboard navigation for accordions
      header.addEventListener('keydown', this.handleAccordionKeydown.bind(this));
    });
  }

  /**
   * Activates the selected tab and deactivates all others
   * @param {HTMLElement} tabButton - The tab button to activate
   */
  activateTab(tabButton) {
    const tabId = tabButton.getAttribute('data-tab-id');
    const tabPanel = document.getElementById(tabId);
    
    // Get currently active tab panel
    const activeTabPanel = this.querySelector('.faqs__tab-content.active');
    
    // Add transition styles if not already added
    if (!document.getElementById('faqs-tab-transitions')) {
      const style = document.createElement('style');
      style.id = 'faqs-tab-transitions';
      style.textContent = `
        .faqs__tab-content {
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.3s ease, transform 0.3s ease;
        }
        .faqs__tab-content.active {
          opacity: 1;
          transform: translateY(0);
        }
      `;
      document.head.appendChild(style);
    }

    // Update tab buttons
    this.tabButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
      btn.setAttribute('tabindex', '-1');
    });

    // Activate selected tab button
    tabButton.classList.add('active');
    tabButton.setAttribute('aria-selected', 'true');
    tabButton.setAttribute('tabindex', '0');
    
    // Animate tab transition
    if (activeTabPanel && activeTabPanel !== tabPanel) {
      // Fade out current tab
      activeTabPanel.style.opacity = '0';
      activeTabPanel.style.transform = 'translateY(10px)';
      
      // After animation completes, hide previous tab and show new tab
      setTimeout(() => {
        // Hide all tab panels
        this.tabContents.forEach(content => {
          content.classList.remove('active');
          content.setAttribute('hidden', '');
        });
        
        // Show and animate the selected tab panel
        tabPanel.classList.add('active');
        tabPanel.removeAttribute('hidden');
        
        // Force reflow to ensure animation works
        void tabPanel.offsetWidth;
        
        // Fade in new tab
        tabPanel.style.opacity = '1';
        tabPanel.style.transform = 'translateY(0)';
      }, 300); // Match the transition duration
    } else {
      // First load or same tab clicked, no animation needed
      this.tabContents.forEach(content => {
        content.classList.remove('active');
        content.setAttribute('hidden', '');
      });
      
      tabPanel.classList.add('active');
      tabPanel.removeAttribute('hidden');
      tabPanel.style.opacity = '1';
      tabPanel.style.transform = 'translateY(0)';
    }
  }

  /**
   * Handles keyboard navigation for tabs
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleTabKeydown(event) {
    let index = Array.from(this.tabButtons).indexOf(event.target);
    let nextTab;

    switch (event.key) {
      case 'ArrowRight':
        nextTab = this.tabButtons[(index + 1) % this.tabButtons.length];
        nextTab.focus();
        this.activateTab(nextTab);
        event.preventDefault();
        break;
      case 'ArrowLeft':
        nextTab = this.tabButtons[(index - 1 + this.tabButtons.length) % this.tabButtons.length];
        nextTab.focus();
        this.activateTab(nextTab);
        event.preventDefault();
        break;
      case 'Home':
        nextTab = this.tabButtons[0];
        nextTab.focus();
        this.activateTab(nextTab);
        event.preventDefault();
        break;
      case 'End':
        nextTab = this.tabButtons[this.tabButtons.length - 1];
        nextTab.focus();
        this.activateTab(nextTab);
        event.preventDefault();
        break;
    }
  }

  /**
   * Toggles the accordion open/closed state
   * @param {HTMLElement} header - The accordion header to toggle
   * @param {boolean|null} forceState - Force a specific state (true = open, false = closed, null = toggle)
   */
  toggleAccordion(header, forceState = null) {
    const accordionItem = header.closest('.faqs__accordion-item');
    const content = accordionItem.querySelector('.faqs__accordion-content');
    const icon = header.querySelector('.faqs__accordion-icon');
    const srText = header.querySelector('.visually-hidden');

    // Determine if we're opening or closing
    const isOpen = forceState !== null ? forceState : !accordionItem.classList.contains('open');

    // Close all accordions first
    this.querySelectorAll('.faqs__accordion-item').forEach(item => {
      const itemHeader = item.querySelector('.faqs__accordion-header');
      const itemContent = item.querySelector('.faqs__accordion-content');
      const itemIcon = itemHeader.querySelector('.faqs__accordion-icon');
      const itemSrText = itemHeader.querySelector('.visually-hidden');

      item.classList.remove('open');
      itemHeader.setAttribute('aria-expanded', 'false');
      itemContent.setAttribute('hidden', '');
      itemContent.style.maxHeight = null;
      // Remove rotation from chevron
      if (itemIcon) {
        const chevron = itemIcon.querySelector('.accordion-chevron');
        if (chevron) {
          chevron.style.transform = 'rotate(0deg)';
        }
      }
      itemSrText.textContent = 'Click to open this accordion';
    });

    // If we're opening this accordion
    if (isOpen) {
      accordionItem.classList.add('open');
      header.setAttribute('aria-expanded', 'true');
      content.removeAttribute('hidden');
      content.style.maxHeight = content.scrollHeight + 'px';
      // Rotate chevron 180 degrees when open
      if (icon) {
        const chevron = icon.querySelector('.accordion-chevron');
        if (chevron) {
          chevron.style.transform = 'rotate(180deg)';
        }
      }
      srText.textContent = 'Click to close this accordion';
    }
  }

  /**
   * Handles keyboard navigation for accordions
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleAccordionKeydown(event) {
    const accordionItems = this.querySelectorAll('.faqs__accordion-item');
    const headers = Array.from(accordionItems).map(item => item.querySelector('.faqs__accordion-header'));
    const index = headers.indexOf(event.target);

    switch (event.key) {
      case 'ArrowDown':
        if (index < headers.length - 1) {
          headers[index + 1].focus();
          event.preventDefault();
        }
        break;
      case 'ArrowUp':
        if (index > 0) {
          headers[index - 1].focus();
          event.preventDefault();
        }
        break;
      case 'Home':
        headers[0].focus();
        event.preventDefault();
        break;
      case 'End':
        headers[headers.length - 1].focus();
        event.preventDefault();
        break;
      case 'Enter':
      case ' ':
        this.toggleAccordion(event.target);
        event.preventDefault();
        break;
    }
  }
}

// Register the custom element
customElements.define('faqs-section', FaqsSection);

// Initialize the FAQs section when the DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Find all FAQs sections and initialize them
  document.querySelectorAll('.faqs').forEach(section => {
    if (!section.classList.contains('faqs-initialized')) {
      section.classList.add('faqs-initialized');
      
      // If the section doesn't already have the custom element
      if (section.tagName.toLowerCase() !== 'faqs-section') {
        // Create a new FaqsSection instance
        const faqsSection = new FaqsSection();
        
        // Copy properties from the section to the instance
        faqsSection.tabButtons = section.querySelectorAll('.faqs__tab-button');
        faqsSection.tabContents = section.querySelectorAll('.faqs__tab-content');
        faqsSection.accordionHeaders = section.querySelectorAll('.faqs__accordion-header');
        
        // Initialize the instance
        faqsSection.init();
      }
    }
  });
});